import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, TextField, Menu, MenuItem, FormControl, InputLabel, IconButton, InputAdornment, Popover, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { getStringOperators } from '../../list.functions';
import { useTranslation } from 'react-i18next';
import FilterTagsInput, { InFilterCompactChip } from './FilterTagsInput';

const normalizeValueForOperator = (op, v) => {
    if (op === 'in') {
        if (Array.isArray(v)) return v;
        if (v == null || v === '') return [];
        return [String(v)];
    }
    if (v == null || v === undefined) return '';
    if (Array.isArray(v)) return v[0] ?? '';
    return typeof v === 'string' ? v : String(v);
};

const StringFilter = ({ item, applyValue, onFilterOperatorChange }) => {
    const [operator, setOperator] = useState(item?.operator || 'equals');
    const [value, setValue] = useState(() => normalizeValueForOperator(item?.operator || 'equals', item?.value));
    const [focused, setFocused] = useState(false);
    const [preventBlur, setPreventBlur] = useState(false);
    const [inEditorOpen, setInEditorOpen] = useState(false);
    const [inEditorAnchor, setInEditorAnchor] = useState(null);
    const { t } = useTranslation();
    const operatorList = getStringOperators(t);

    const inputRef = useRef(null);
    const inStripRef = useRef(null);

    useEffect(() => {
        setOperator(item?.operator || 'equals');
    }, [item?.operator]);

    useEffect(() => {
        const op = item?.operator || 'equals';
        setValue(normalizeValueForOperator(op, item?.value));
    }, [item?.value, item?.operator]);

    const handleKeyDown = (event) => {
        event.target.focus();
        setPreventBlur(true);
    };

    const handleFocus = () => {
        setFocused(true);
    };

    const handleBlur = (event) => {
        if (preventBlur) {
            event.target.focus();
            setPreventBlur(false);
        } else {
            setFocused(false);
        }
    };

    const handleValueChange = (event) => {
        const newValue = event.target.value;

        setValue(newValue);
        if (operator !== 'isEmpty' && operator !== 'isNotEmpty') {
            applyValue({ ...item, operator, value: newValue });
        }
        setFocused(true);
    };

    const handleInValuesChange = (nextValues) => {
        setValue(nextValues);
        applyValue({ ...item, operator: 'in', value: nextValues });
        setFocused(true);
    };

    const handleValueEmpty = (event) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        if (operator === 'in') {
            setValue([]);
            applyValue({ ...item, operator, value: [] });
            return;
        }

        setValue('');

        if (operator !== 'isEmpty' && operator !== 'isNotEmpty') {
            applyValue({ ...item, value: '', operator: item?.operator });
        } else {
            applyValue({ ...item, value: '', operator: 'contains' });
        }
    };

    const isValueInputDisabled = operator === 'isEmpty' || operator === 'isNotEmpty';
    const isInOperator = operator === 'in';
    const inValues = Array.isArray(value) ? value : [];
    const isValueEmpty = isInOperator
        ? inValues.length === 0
        : value === '' || value === null || value === undefined;

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpenOperatorMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseOperatorMenu = () => {
        setAnchorEl(null);
    };

    const handleOperatorChange = (nextOperator) => {
        setOperator(nextOperator);

        if (onFilterOperatorChange) {
            onFilterOperatorChange(nextOperator);
        }

        if (nextOperator === 'isEmpty' || nextOperator === 'isNotEmpty') {
            applyValue({ ...item, operator: nextOperator, value: ' ' });
            handleCloseOperatorMenu();
            return;
        }

        if (nextOperator === 'in') {
            let migrated = [];
            if (typeof value === 'string' && value.trim()) {
                migrated = [value.trim()];
            } else if (Array.isArray(value) && value.length) {
                migrated = [...value];
            }
            setValue(migrated);
            applyValue({ ...item, operator: nextOperator, value: migrated });
            handleCloseOperatorMenu();
            return;
        }

        const strVal = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
        setValue(strVal);
        applyValue({ ...item, operator: nextOperator, value: strVal });
        handleCloseOperatorMenu();
    };

    const clearButton = !isValueEmpty && !isValueInputDisabled ? (
        <InputAdornment position="end">
            <IconButton size="small" sx={{ padding: '4px' }} onClick={(e) => handleValueEmpty(e)}>
                <ClearIcon fontSize="small" />
            </IconButton>
        </InputAdornment>
    ) : null;

    const openInEditor = useCallback(() => {
        if (isValueInputDisabled) return;
        setFocused(true);
        setInEditorAnchor(inStripRef.current);
        setInEditorOpen(true);
    }, [isValueInputDisabled]);

    const closeInEditor = useCallback(() => {
        setInEditorOpen(false);
        setInEditorAnchor(null);
    }, []);

    return (
        <FormControl
            variant="standard"
            size="small"
            className="mtska-list-filter-wrapper"
            sx={
                isInOperator
                    ? {
                          mt: '2px',
                          alignItems: 'stretch',
                          width: '100%',
                          '& .MuiInputLabel-root': { top: 2 },
                      }
                    : undefined
            }
        >
            <InputLabel shrink={focused || !isValueEmpty}>{t(operator)}</InputLabel>

            {isInOperator ? (
                <>
                    <Box
                        ref={(n) => {
                            inStripRef.current = n;
                            inputRef.current = n;
                        }}
                        className="mtska-list-filter-value"
                        onClick={openInEditor}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openInEditor();
                            }
                        }}
                        role="button"
                        tabIndex={isValueInputDisabled ? -1 : 0}
                        aria-expanded={inEditorOpen}
                        aria-haspopup="dialog"
                        sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            overflow: 'hidden',
                            alignItems: 'center',
                            gap: '6px',
                            minHeight: 32,
                            pt: '6px',
                            pb: '2px',
                            cursor: isValueInputDisabled ? 'default' : 'pointer',
                            borderBottom: 1,
                            borderColor: 'divider',
                            width: '100%',
                            boxSizing: 'border-box',
                            '&:focus-visible': {
                                outline: '2px solid',
                                outlineColor: 'primary.main',
                                outlineOffset: 2,
                            },
                        }}
                    >
                        {inValues.length === 0 ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                sx={{ fontSize: '0.8125rem', userSelect: 'none' }}
                            >
                                {t('filter_in_open_editor', { defaultValue: 'Clicca per impostare i valori…' })}
                            </Typography>
                        ) : (
                            inValues.map((v, i) => <InFilterCompactChip key={`${i}-${v}`} label={v} />)
                        )}
                    </Box>

                    <Popover
                        open={inEditorOpen}
                        anchorEl={inEditorAnchor}
                        onClose={closeInEditor}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{
                            elevation: 8,
                            sx: {
                                p: 1.5,
                                minWidth: 320,
                                maxWidth: 'min(440px, calc(100vw - 32px))',
                                mt: 0.5,
                            },
                            onClick: (e) => e.stopPropagation(),
                        }}
                    >
                        <FilterTagsInput
                            values={inValues}
                            onValuesChange={handleInValuesChange}
                            disabled={isValueInputDisabled}
                            tagTextMaxWidth="full"
                            endAdornment={clearButton}
                            textFieldProps={{
                                autoFocus: true,
                                placeholder: t('Value'),
                                onFocus: handleFocus,
                                onBlur: handleBlur,
                                onKeyDown: handleKeyDown,
                            }}
                            panelStyle={{
                                border: 'none',
                                p: 0,
                                minHeight: 0,
                                background: 'transparent',
                            }}
                        />
                    </Popover>
                </>
            ) : (
                <TextField
                    variant="standard"
                    size="small"
                    className="mtska-list-filter-value"
                    value={value}
                    onChange={handleValueChange}
                    inputRef={inputRef}
                    disabled={isValueInputDisabled}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    InputProps={clearButton ? { endAdornment: clearButton } : undefined}
                />
            )}

            <IconButton size="small" onClick={handleOpenOperatorMenu}>
                <FilterListIcon fontSize="small" />
            </IconButton>

            <Menu anchorEl={anchorEl} open={open} onClose={handleCloseOperatorMenu}>
                {(operatorList ?? []).map((op) => (
                    <MenuItem key={op.value} value={op.value} onClick={() => handleOperatorChange(op.value)}>
                        {op.label}
                    </MenuItem>
                ))}
            </Menu>
        </FormControl>
    );
};

export default StringFilter;
